/**
 * One table describing every channel, parsed from its name.
 *
 * The transport asks four questions about every frame that arrives — is this
 * channel sequenced, is it private, how may it be coalesced, and what makes two
 * of its payloads the same thing — and the answers are properties of the
 * channel, not of the code path that happens to be handling it. Spreading them
 * across `connection.ts`, `sequencer.ts`, `dedupe.ts` and the flush is how
 * `ticker` ends up exempt from gap recovery in one file and not in another.
 *
 * Sources: `asyncapi.yaml` channel definitions (authoritative for names and
 * cadences), docs/03 §4.3 (the channel table) and §4.4 (dedupe keys).
 *
 * **`ticker` carries no `seq` by design** and is the one channel that must be
 * exempted explicitly rather than by treating a missing `seq` as a gap: every
 * frame is a complete replacement, so a dropped one costs a single stale tick
 * and is repaired 100 ms later. Acting on it would turn a dropped packet into an
 * HTTP request storm at 10 Hz.
 */

import type { CoalesceMode } from '@/lib/buffer'
import type { ChannelName } from '../protocol'

export type ChannelKind =
  | 'heartbeat'
  | 'index'
  | 'series'
  | 'market'
  | 'book'
  | 'trades'
  | 'ticker'
  | 'candles'
  | 'orders'
  | 'fills'
  | 'positions'
  | 'balance'
  | 'settlements'
  | 'notifications'
  | 'chat'

/**
 * How two payloads on one channel are recognised as the same thing.
 *
 * - `sequence` — public streams. `seq` is the only identity that matters and the
 *   sequencer handles it; nothing further to dedupe.
 * - `versioned` — apply iff the ordering key is **strictly greater** than the one
 *   held. Survives reordering, which an id set does not.
 * - `immutable` — the object never changes, so first delivery wins and every
 *   later one is dropped on sight of its id. Two channels, since §F31 took
 *   `fills` out: a settlement and a notification are decisions, and a decision
 *   is not revised.
 */
export type DedupeStrategy = 'sequence' | 'versioned' | 'immutable'

export interface ChannelDescriptor {
  kind: ChannelKind
  /** Private channels need an authenticated connection and are dropped whole on
   *  `SESSION_EXPIRED` without the socket closing (asyncapi §5.9). */
  private: boolean
  /** `false` for `ticker` only. */
  sequenced: boolean
  coalesce: CoalesceMode
  dedupe: DedupeStrategy
  /** Field naming the object's identity, for `versioned` and `immutable`. */
  idField?: string
  /**
   * Field that orders two payloads with the same id, for `versioned`.
   *
   * `positions` orders on `updated_at_ms` and **not** on `version`: docs/03 §4.4
   * and docs/04 §9.4 both name `version`, and neither `openapi.Position` nor
   * `asyncapi.PositionData` defines such a field. `updated_at_ms` is in both and
   * carries the same monotonic-per-entity guarantee. Register §F15d.
   */
  orderField?: string
  /** Key of the entry a `keyed` buffer coalesces on. */
  bufferKey?: string
}

export interface ParsedChannel extends ChannelDescriptor {
  name: string
  /** `market.{id}`, `book.{id}`, `trades.{id}`, `ticker.{id}`, `candles.{id}.{i}` */
  marketId?: string
  /** `index.{symbol}` */
  symbol?: string
  /** `series.{id}` */
  seriesId?: string
  /** `candles.{id}.{interval}` */
  interval?: string
  /** `chat.{asset}` */
  asset?: string
}

/**
 * The table. Every entry traceable to a row of docs/03 §4.3 and §4.4.
 *
 * `orders` and `positions` coalesce `keyed` because two frames for the same
 * entity inside one animation frame are the same row rendered twice; `fills`,
 * `trades`, `settlements` and `notifications` coalesce `queue` because each
 * frame is a distinct fact and dropping one loses money, a print, or a warning.
 * `fills` is `queue` **and** `versioned` since §F31 — different fills must all
 * arrive, and the same fill's mined confirmation must not be mistaken for one.
 * `balance` is `latest` — it is always a complete object, so the newest is the
 * only one worth applying.
 */
const DESCRIPTORS: Record<ChannelKind, ChannelDescriptor> = {
  heartbeat: { kind: 'heartbeat', private: false, sequenced: true, coalesce: 'latest', dedupe: 'sequence' },
  index: { kind: 'index', private: false, sequenced: true, coalesce: 'latest', dedupe: 'sequence' },
  series: { kind: 'series', private: false, sequenced: true, coalesce: 'queue', dedupe: 'sequence' },
  market: { kind: 'market', private: false, sequenced: true, coalesce: 'queue', dedupe: 'sequence' },
  book: { kind: 'book', private: false, sequenced: true, coalesce: 'queue', dedupe: 'sequence' },
  trades: { kind: 'trades', private: false, sequenced: true, coalesce: 'queue', dedupe: 'sequence' },
  // One room per asset (§F137): every frame a batch of new messages, so
  // `queue` — dropping one would lose what somebody said.
  chat: { kind: 'chat', private: false, sequenced: true, coalesce: 'queue', dedupe: 'sequence' },
  // The exemption, in the one place that decides it.
  ticker: { kind: 'ticker', private: false, sequenced: false, coalesce: 'latest', dedupe: 'sequence' },
  candles: {
    kind: 'candles',
    private: false,
    sequenced: true,
    coalesce: 'keyed',
    dedupe: 'versioned',
    // Upsert by bucket start. One name in both specs since §F16 — `asyncapi`
    // called it `t_open_ms` and `openapi` `open_time_ms`, which meant the chart's
    // REST backfill and its forming bar could not be merged without translating
    // between them.
    idField: 'open_time_ms',
    orderField: 'close_time_ms',
    bufferKey: 'open_time_ms',
  },
  orders: {
    kind: 'orders',
    private: true,
    sequenced: true,
    coalesce: 'keyed',
    dedupe: 'versioned',
    idField: 'order_id',
    orderField: 'version',
    bufferKey: 'order_id',
  },
  fills: {
    kind: 'fills',
    private: true,
    sequenced: true,
    // Still `queue`, not `keyed`: two *different* fills in one animation frame
    // are two prints and dropping either loses money. The same fill twice is
    // handled a layer up, by `versioned` dedupe — a v1 that has already been
    // superseded by v2 never reaches the buffer at all.
    coalesce: 'queue',
    // **Versioned since §F31, and it was `immutable` for ten phases.** A fill
    // has two tiers under D-B1: matched at the sequencer, then mined when its
    // `matchOrders` transaction lands. The second arrives as the same `fill_id`
    // at a higher `version` carrying `settlement_tx_hash`. Deduping on the id
    // alone dropped it on sight and the user never learned the money moved.
    dedupe: 'versioned',
    idField: 'fill_id',
    orderField: 'version',
    bufferKey: 'fill_id',
  },
  positions: {
    kind: 'positions',
    private: true,
    sequenced: true,
    coalesce: 'keyed',
    dedupe: 'versioned',
    // `market_id` alone, never `market_id + outcome`: a position object carries
    // both `up_shares` and `down_shares`, so outcome is a field inside it rather
    // than part of its identity. Keying on the pair would let a stale UP frame
    // and a fresh DOWN frame coexist as two rows (docs/03 §4.4).
    idField: 'market_id',
    orderField: 'updated_at_ms',
    bufferKey: 'market_id',
  },
  balance: {
    kind: 'balance',
    private: true,
    sequenced: true,
    coalesce: 'latest',
    dedupe: 'versioned',
    idField: 'account',
    orderField: 'version',
  },
  settlements: {
    kind: 'settlements',
    private: true,
    sequenced: true,
    coalesce: 'queue',
    dedupe: 'immutable',
    idField: 'settlement_id',
  },
  notifications: {
    kind: 'notifications',
    private: true,
    sequenced: true,
    coalesce: 'queue',
    dedupe: 'immutable',
    idField: 'notification_id',
  },
}

const PRIVATE_KINDS = ['orders', 'fills', 'positions', 'balance', 'settlements', 'notifications'] as const

/** The six, in the order the cold load subscribes them (docs/03 §5.2). */
export const PRIVATE_CHANNELS: readonly ChannelName[] = PRIVATE_KINDS

export class ChannelNameError extends Error {
  override readonly name = 'ChannelNameError'
}

/**
 * Parse a channel name into its descriptor.
 *
 * Unknown names throw rather than degrading to a default: a channel the client
 * did not ask for cannot arrive, and one it cannot classify would be routed by
 * guesswork — which for `sequenced` means either a phantom gap or a silently
 * swallowed one.
 */
export function parseChannel(name: string): ParsedChannel {
  const dot = name.indexOf('.')
  const head = dot === -1 ? name : name.slice(0, dot)
  const tail = dot === -1 ? '' : name.slice(dot + 1)

  if (dot === -1) {
    const descriptor = DESCRIPTORS[head as ChannelKind]
    if (!descriptor || (head !== 'heartbeat' && !isPrivateKind(head))) {
      throw new ChannelNameError(`unknown channel: ${JSON.stringify(name)}`)
    }
    return { ...descriptor, name }
  }

  switch (head) {
    case 'index':
      return { ...DESCRIPTORS.index, name, symbol: tail }
    case 'series':
      return { ...DESCRIPTORS.series, name, seriesId: tail }
    case 'market':
      return { ...DESCRIPTORS.market, name, marketId: tail }
    case 'book':
      return { ...DESCRIPTORS.book, name, marketId: tail }
    case 'trades':
      return { ...DESCRIPTORS.trades, name, marketId: tail }
    case 'ticker':
      return { ...DESCRIPTORS.ticker, name, marketId: tail }
    case 'chat':
      return { ...DESCRIPTORS.chat, name, asset: tail }
    case 'candles': {
      // `candles.{market_id}.{interval}` — the market id itself contains no dot,
      // so the last segment is the interval.
      const split = tail.lastIndexOf('.')
      if (split === -1) throw new ChannelNameError(`candles channel needs an interval: ${JSON.stringify(name)}`)
      return {
        ...DESCRIPTORS.candles,
        name,
        marketId: tail.slice(0, split),
        interval: tail.slice(split + 1),
      }
    }
    default:
      throw new ChannelNameError(`unknown channel: ${JSON.stringify(name)}`)
  }
}

export function isPrivateChannel(name: string): boolean {
  const dot = name.indexOf('.')
  return dot === -1 && isPrivateKind(name)
}

function isPrivateKind(name: string): boolean {
  return (PRIVATE_KINDS as readonly string[]).includes(name)
}

/** Channel names for one market's depth panels — the set that churns at every
 *  roll and is swapped in a single batched frame (docs/03 §6.2). */
export function depthChannelsFor(marketId: string, interval = '1s'): ChannelName[] {
  return [
    `book.${marketId}`,
    `trades.${marketId}`,
    `ticker.${marketId}`,
    `candles.${marketId}.${interval}`,
  ] as ChannelName[]
}
